/*
  Freakmount photo viewer. A native vertical scroll keeps every photo of the colour
  together; a modal dialog puts it above app widgets and contains focus.
*/
window.FmPhotoZoom = class FmPhotoZoom {
  constructor(gallery) {
    this.gallery = gallery;
    // Embla's capture listener runs on the viewport first and swallows drag clicks.
    // Intercept below it, before Dawn's button listener opens its modal.
    gallery.mediaList?.addEventListener('click', (event) => {
      const opener = event.target.closest('.product__modal-opener--image[data-fm-zoom-src]');
      if (!opener || !gallery.querySelector('[data-fm-photo-zoom]')) return;
      event.preventDefault();
      event.stopPropagation();
      // A click from the keyboard (Enter or Space) carries no click count.
      this.open(opener, event.detail === 0);
    }, true);
  }

  open(opener, fromKeyboard) {
    if (this.dialog) return;
    this.fromKeyboard = fromKeyboard;
    const gallery = this.gallery;
    this.photos = Array.from(gallery.mediaList.children).flatMap((slide) => {
      const source = slide.querySelector('.product__modal-opener--image[data-fm-zoom-src]');
      return source ? [{ slide, source }] : [];
    });
    this.index = this.photos.findIndex((photo) => photo.source === opener);
    if (this.index < 0) return;
    this.opener = opener.querySelector('.product__media-toggle');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.dialog = gallery.querySelector('[data-fm-photo-zoom]').content.firstElementChild.cloneNode(true);
    this.scroller = this.dialog.querySelector('.fm-photo-zoom__scroll');
    this.strip = this.dialog.querySelector('.fm-photo-zoom__strip');
    this.thumbnails = this.dialog.querySelector('.fm-photo-zoom__thumbnails');
    this.photos.forEach((photo, index) => {
      const image = document.createElement('img');
      const { fmZoomSrc, fmZoomWidth, fmZoomHeight } = photo.source.dataset;
      const width = Number(fmZoomWidth);
      const height = Number(fmZoomHeight);
      image.width = width;
      image.height = height;
      image.style.setProperty('--fm-photo-ratio', width / height);
      image.alt = photo.source.querySelector('img').alt;
      image.draggable = false;
      image.sizes = '(min-width: 750px) calc(97vw - 128px), 100vw';
      // Include the original itself: many photos stop between the standard widths.
      image.srcset = [550, 750, 1100, 1500, 2000, 2500, 3000, 4000, 5000]
        .filter((size) => size < width)
        .map((size) => {
          const url = new URL(fmZoomSrc, document.baseURI);
          url.searchParams.set('width', size);
          return `${url.href} ${size}w`;
        }).concat(`${fmZoomSrc} ${width}w`).join(', ');
      image.src = fmZoomSrc;
      photo.image = image;
      this.strip.append(image);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fm-photo-zoom__thumbnail';
      button.setAttribute('aria-label', this.dialog.dataset.fmImageLabel.replace('[index]', index + 1));
      const thumbnail = document.createElement('img');
      thumbnail.width = width;
      thumbnail.height = height;
      thumbnail.alt = '';
      thumbnail.draggable = false;
      const url = new URL(fmZoomSrc, document.baseURI);
      url.searchParams.set('width', Math.min(width, 192));
      thumbnail.src = url.href;
      button.append(thumbnail);
      button.addEventListener('click', () => this.scrollToPhoto(index, !this.reducedMotion.matches));
      photo.button = button;
      this.thumbnails.append(button);
    });

    this.scrollPosition = { x: window.scrollX, y: window.scrollY };
    this.savedStyles = [];
    const setStyle = (element, name, value) => {
      this.savedStyles.push([element, name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
      element.style.setProperty(name, value);
    };
    // Fixed-body locking also works on iOS, where overflow:hidden alone can leak a pan.
    setStyle(document.documentElement, 'overflow', 'hidden');
    setStyle(document.documentElement, 'scroll-behavior', 'auto');
    setStyle(document.body, 'position', 'fixed');
    setStyle(document.body, 'top', `${-this.scrollPosition.y}px`);
    setStyle(document.body, 'left', `${-this.scrollPosition.x}px`);
    setStyle(document.body, 'width', '100%');
    gallery.classList.add('fm-photo-zoom-open');
    document.body.append(this.dialog);
    this.dialog.showModal();
    const closeButton = this.dialog.querySelector('.fm-photo-zoom__close');
    // Focus the dialog, as Dawn's pop-up does: on the × it drew a focus ring after every tap.
    this.dialog.focus({ preventScroll: true });
    closeButton.addEventListener('click', () => this.close());
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (!gallery.mql.matches || event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const buttons = [closeButton, ...this.photos.map((photo) => photo.button)];
      const index = buttons.indexOf(document.activeElement);
      event.preventDefault();
      const next = index < 0 ? (event.shiftKey ? buttons.length - 1 : 0) :
        (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next].focus({ preventScroll: true });
      if (next > 0) this.revealThumbnail(buttons[next]);
    });

    this.layoutPhotos();
    this.scroller.addEventListener('scroll', () => this.updateCurrent(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.layoutPhotos());
    this.resizeObserver.observe(this.scroller);
  }

  layoutPhotos() {
    // Leave enough trailing space to align even the last short photo with the top.
    const last = this.photos[this.photos.length - 1].image;
    this.strip.style.paddingBottom = `${Math.max(0, this.scroller.clientHeight - last.clientHeight)}px`;
    this.photos.forEach(({ image }) => { image.sizes = `${Math.ceil(image.getBoundingClientRect().width)}px`; });
    this.scrollToPhoto(this.index);
    this.updateCurrent();
  }

  scrollToPhoto(index, smooth = false) {
    this.scroller.scrollTo({ top: this.photos[index].image.offsetTop, behavior: smooth ? 'smooth' : 'instant' });
  }

  revealThumbnail(button) {
    const rail = this.thumbnails.getBoundingClientRect();
    const rect = button.getBoundingClientRect();
    const desktop = this.gallery.mql.matches;
    const start = desktop ? rect.top - rail.top : rect.left - rail.left;
    const end = desktop ? rect.bottom - rail.bottom : rect.right - rail.right;
    const delta = start < 0 ? start : Math.max(0, end);
    if (delta) this.thumbnails.scrollBy({ [desktop ? 'top' : 'left']: delta, behavior: 'instant' });
  }

  updateCurrent() {
    const viewport = this.scroller.getBoundingClientRect();
    let largest = 0;
    this.photos.forEach(({ image }, index) => {
      const rect = image.getBoundingClientRect();
      const area = Math.max(0, Math.min(rect.right, viewport.right) - Math.max(rect.left, viewport.left)) *
        Math.max(0, Math.min(rect.bottom, viewport.bottom) - Math.max(rect.top, viewport.top));
      if (area > largest) {
        largest = area;
        this.index = index;
      }
    });
    this.photos.forEach(({ button }, index) => {
      if (index === this.index) {
        if (button.getAttribute('aria-current') !== 'true') {
          button.setAttribute('aria-current', 'true');
          this.revealThumbnail(button);
        }
      } else button.removeAttribute('aria-current');
    });
  }

  close() {
    if (!this.dialog) return;
    this.updateCurrent();
    this.resizeObserver.disconnect();
    const gallery = this.gallery;
    const slide = this.photos[this.index].slide;
    const index = gallery.embla?.slideNodes().indexOf(slide);
    if (!gallery.mql.matches && index >= 0) {
      gallery.embla.scrollTo(index, true);
      gallery.onEmblaSelect();
      // Finish the height change while it is hidden, before restoring its transition.
      gallery.viewport.getBoundingClientRect();
    }
    this.dialog.close();
    this.dialog.remove();
    this.dialog = null;
    gallery.classList.remove('fm-photo-zoom-open');
    // Restore only the properties we changed, without overwriting other app styles.
    const restore = ([element, name, value, priority]) => {
      if (value) element.style.setProperty(name, value, priority);
      else element.style.removeProperty(name);
    };
    this.savedStyles.filter((entry) => entry[1] !== 'scroll-behavior').forEach(restore);
    // Focus goes back to the photo only for a keyboard user. After a click, the dialog's own restore
    // plus Escape counting as a key made Dawn draw its ring on the photo (`.focused`, :focus-visible).
    if (this.fromKeyboard) this.opener?.focus({ preventScroll: true });
    else {
      if (document.activeElement === this.opener) this.opener.blur();
      this.opener?.classList.remove('focused');
    }
    window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
    this.savedStyles.filter((entry) => entry[1] === 'scroll-behavior').forEach(restore);
  }
};
